import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import React, { useState, useCallback, useRef } from "react";
import { createRoot } from "react-dom/client";

interface PictureInPictureOptions {
  width?: number;
  height?: number;
}

interface UsePictureInPictureReturn {
  isSupported: boolean;
  isOpen: boolean;
  openPiP: (content: React.ReactElement, options?: PictureInPictureOptions) => Promise<void>;
  updatePiP: (content: React.ReactElement) => void;
  closePiP: () => void;
  pipWindow: Window | null;
}

export const usePictureInPicture = (theme: any): UsePictureInPictureReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const rootRef = useRef<any>(null);
  const emotionCacheRef = useRef<any>(null);

  // Check if Document Picture-in-Picture API is supported
  const isSupported = "documentPictureInPicture" in window;

  const openPiP = useCallback(async (
    content: React.ReactElement,
    options: PictureInPictureOptions = {},
  ) => {
    if (!isSupported) {
      console.warn("Document Picture-in-Picture is not supported in this browser");
      return;
    }

    try {
      // Close existing PiP window if open
      if (pipWindow) {
        pipWindow.close();
      }

      // Request Picture-in-Picture window
      const newPipWindow = await (window as any).documentPictureInPicture.requestWindow({
        width: options.width || 320,
        height: options.height || 480,
      });

      // Set up document structure first
      newPipWindow.document.head.innerHTML = `
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <base href="${window.location.origin}">
        <title>FitCheckr - Picture in Picture</title>
      `;

      // Copy stylesheets from parent window to PiP window
      // Clone link and style elements instead of reading cssRules to avoid CORS issues
      const styleElements = document.querySelectorAll('link[rel="stylesheet"], style');
      styleElements.forEach((element) => {
        try {
          const clonedElement = element.cloneNode(true) as HTMLElement;
          newPipWindow.document.head.appendChild(clonedElement);
        } catch (error) {
          console.warn("Could not copy style element:", error);
        }
      });

      // Create Emotion cache for the PiP window
      const emotionInsertionPoint = newPipWindow.document.createElement("meta");
      emotionInsertionPoint.setAttribute("name", "emotion-insertion-point");
      emotionInsertionPoint.setAttribute("content", "");
      newPipWindow.document.head.appendChild(emotionInsertionPoint);

      const emotionCache = createCache({
        key: "pip-emotion",
        container: newPipWindow.document.head,
        insertionPoint: emotionInsertionPoint,
      });
      emotionCacheRef.current = emotionCache;

      // Set up the document structure
      newPipWindow.document.body.style.margin = "0";
      newPipWindow.document.body.style.padding = "0";
      newPipWindow.document.body.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

      // Create root container
      const container = newPipWindow.document.createElement("div");
      container.style.width = "100%";
      container.style.height = "100%"; // Use 100% instead of 100vh to avoid scroll jumps
      newPipWindow.document.body.appendChild(container);

      // Create React root and render content with theme and emotion cache
      rootRef.current = createRoot(container);
      rootRef.current.render(
        React.createElement(CacheProvider, { value: emotionCache }, [
          React.createElement(ThemeProvider, { theme, key: "theme" }, [
            React.createElement(CssBaseline, { key: "baseline" }),
            React.cloneElement(content, { key: "pip-content" }),
          ]),
        ]),
      );

      // Set up event listeners
      newPipWindow.addEventListener("beforeunload", () => {
        setIsOpen(false);
        setPipWindow(null);
        rootRef.current = null;
        emotionCacheRef.current = null;
      });

      // Update state
      setPipWindow(newPipWindow);
      setIsOpen(true);

      console.log("✅ Picture-in-Picture window opened successfully");
    } catch (error) {
      console.error("❌ Failed to open Picture-in-Picture window:", error);
    }
  }, [isSupported, pipWindow, theme]);

  const updatePiP = useCallback((content: React.ReactElement) => {
    if (rootRef.current && isOpen && emotionCacheRef.current) {
      rootRef.current.render(
        React.createElement(CacheProvider, { value: emotionCacheRef.current }, [
          React.createElement(ThemeProvider, { theme, key: "theme" }, [
            React.createElement(CssBaseline, { key: "baseline" }),
            React.cloneElement(content, { key: "pip-content" }),
          ]),
        ]),
      );
    }
  }, [theme, isOpen]);

  const closePiP = useCallback(() => {
    if (pipWindow) {
      pipWindow.close();
      setPipWindow(null);
      setIsOpen(false);
      rootRef.current = null;
      emotionCacheRef.current = null;
    }
  }, [pipWindow]);

  return {
    isSupported,
    isOpen,
    openPiP,
    updatePiP,
    closePiP,
    pipWindow,
  };
};
